/**
 * @fileoverview Login form component that handles user authentication
 * @module components/auth/LoginForm
 */

import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { FormEvent, useState, useCallback } from "react";
import { useToast } from "../ui/use-toast";
import { useLanguage } from "../../contexts/LanguageContext";
import { Loader2, Mail } from "lucide-react";
import { useSupportEmail } from "../../hooks/useSupportEmail";

// Regular expression to validate email addresses
const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

// Interface for the form's state
interface FormState {
    companyName: string;
    associateNumber: number | string;
    contactName: string;
    email: string;
    message: string;
}

// Interface for form validation errors
interface FormErrors {
  companyName?: string;
  associateNumber?: string;
  contactName?: string;
  email?: string;
  message?: string;
}

/**
 * LoginForm Component
 * @description Handles user authentication through a form interface
 */
export const ParticipationForm = () => {
  // Hooks and State
  const { sendParticipationForm } = useAuth();
  const { toast } = useToast();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { supportEmail } = useSupportEmail();

  const [formData, setFormData] = useState<FormState>({
    companyName: "",
    associateNumber: "",
    contactName: "",
    email: "",
    message: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});

  // Form Validation
  const validateStringField = useCallback(
    (name: string, value: string) => {
      switch (name) {
        case "companyName":
          if (!value) {
            return t("auth.participation.companyName.required");
          }
          return undefined;
        case "contactName":
          if (!value) {
            return t("auth.participation.contactName.required");
          }
          return undefined;
        case "email":
          if (!value) {
            return t("auth.login.email.required");
          }
          if (!EMAIL_REGEX.test(value)) {
            return t("auth.login.email.invalid");
          }
          return undefined;
        case "message":
          if (!value) {
            return t("auth.participation.message.required");
          }
          return undefined;
        default:
          return undefined;
      }
    },
    [t]
  );

  const validateNumberField = useCallback(
    (_name: string, _value: number | string) => {
      // Associate number is optional: any stone-sector company may participate
      // without being an ASSIMAGRA member, so no required/format validation here.
      return undefined;
    },
    []
  );

  const validateForm = useCallback(() => {
    const newErrors = {
      companyName: validateStringField("companyName", formData.companyName),
      associateNumber: validateNumberField("associateNumber", formData.associateNumber),
      contactName: validateStringField("contactName", formData.contactName),
      email: validateStringField("email", formData.email),
      message: validateStringField("message", formData.message),
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== undefined);
  }, [formData, validateStringField]);

  // Event Handlers
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (touched[name]) {
      const error = validateStringField(name, value);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateStringField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched({ email: true, password: true });

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
        const success = await sendParticipationForm({
            email: formData.email,
            companyName: formData.companyName,
            associateNumber:
                formData.associateNumber === "" || formData.associateNumber === null
                    ? null
                    : Number(formData.associateNumber),
            contactName: formData.contactName,
            message: formData.message,
        });

      if (success) {
        toast({
          variant: "default",
          title: t("auth.toasts.participationSuccess.title"),
          description: t("auth.toasts.participationSuccess.description"),
        });
        setTimeout(() => {
          navigate("/");
        }, 500);
      }
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: t("auth.toasts.participationFailed.title"),
        description: err.code === "function-error" && err.message.includes("CORS") 
          ? "Unable to connect to the server. Please try again later or contact support."
          : t("auth.toasts.participationFailed.description"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      <div className="mb-8">
        <h2 className="text-2xl font-semibold mb-2">{t("auth.participation.title")}</h2>
        <p className="text-muted-foreground">{t("auth.participation.subtitle")}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-5">
            <div className="flex w-full gap-x-5">
                {/* Company Name Field */}
                <div className="w-1/2">
                    <Label htmlFor="companyName" className="mb-2.5 block font-normal">
                        {t("auth.participation.companyName.label")}
                        <span className="text-destructive ml-0.5">*</span>
                    </Label>
                    <Input
                        id="companyName"
                        name="companyName"
                        type="text"
                        value={formData.companyName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder={t("auth.participation.companyName.placeholder")}
                        className={`${errors.companyName ? "border-red-500" : ""} placeholder:text-gray-300`}
                    />
                    {errors.companyName && (
                    <div className="flex gap-x-2 items-center mt-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
                        <p className="text-sm text-red-500">{errors.companyName}</p>
                    </div>
                    )}
                </div>

                {/* Associate Number Field */}
                <div className="w-1/2">
                    <Label htmlFor="associateNumber" className="mb-2.5 block font-normal">
                        {t("auth.participation.associateNumber.label")}
                        <span className="text-muted-foreground ml-1 text-sm">
                            ({t("auth.participation.associateNumber.optional")})
                        </span>
                    </Label>
                    <Input
                        id="associateNumber"
                        name="associateNumber"
                        type="number"
                        value={formData.associateNumber}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder={t("auth.participation.associateNumber.placeholder")}
                        className={`${errors.associateNumber ? "border-red-500" : ""} placeholder:text-gray-300`}
                    />
                    {errors.associateNumber && (
                    <div className="flex gap-x-2 items-center mt-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
                        <p className="text-sm text-red-500">{errors.associateNumber}</p>
                    </div>
                    )}
                </div>
            </div>
       
          {/* Contact Name Field */}
          <div>
            <Label htmlFor="contactName" className="mb-2.5 block font-normal">
              {t("auth.participation.contactName.label")}
              <span className="text-destructive ml-0.5">*</span>
            </Label>
            <Input
              id="contactName"
              name="contactName"
              type="text"
              value={formData.contactName}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={t("auth.participation.contactName.placeholder")}
              className={`${errors.contactName ? "border-red-500" : ""} placeholder:text-gray-300`}
            />
            {errors.contactName && (
              <div className="flex gap-x-2 items-center mt-2">
                <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
                <p className="text-sm text-red-500">{errors.contactName}</p>
              </div>
            )}
          </div>

          {/* Email Field */}
          <div>
            <Label htmlFor="email" className="mb-2.5 block font-normal">
              {t("auth.login.email.label")}
              <span className="text-destructive ml-0.5">*</span>
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={t("auth.login.email.placeholder")}
              className={`${errors.email ? "border-red-500" : ""} placeholder:text-gray-300`}
              icon={<Mail className="w-5 h-5" />}
            />
            {errors.email && (
              <div className="flex gap-x-2 items-center mt-2">
                <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
                <p className="text-sm text-red-500">{errors.email}</p>
              </div>
            )}
          </div>

          {/* Message Field */}
          <div>
            <Label htmlFor="message" className="mb-2.5 block font-normal">
              {t("auth.participation.message.label")}
              <span className="text-destructive ml-0.5">*</span>
            </Label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={t("auth.participation.message.placeholder")}
              className={`w-full min-h-[100px] px-3 py-2 border rounded-md text-sm ${
                errors.message ? "border-red-500" : "border-input"
              } placeholder:text-gray-300 focus:outline-none focus:ring-2 focus:ring-[#169C9D] focus:border-transparent`}
            />
            {errors.message && (
              <div className="flex gap-x-2 items-center mt-2">
                <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
                <p className="text-sm text-red-500">{errors.message}</p>
              </div>
            )}
          </div>

        </div>
        
        {/* Participation Link */}
        <div className="text-sm text-center text-gray-600">
            <span>{t("auth.participation.memberText")} </span>
            <Link to="/login" className="text-[#169C9D] hover:underline">
                {t("auth.participation.memberLink")}
            </Link>
        </div>
        

        {/* Submit Button */}
        <Button
          type="submit"
          className="w-full bg-[#169C9D] hover:bg-[#138889] text-white rounded-md h-10"
          disabled={isSubmitting}
        >
          {isSubmitting ? t("auth.participation.button.loading") : t("auth.participation.button.default")}
          {isSubmitting && <Loader2 className="w-4 h-4 ml-2 animate-spin" />}
        </Button>

        {/* Help Link */}
        <div className="text-sm text-center text-gray-600">
            <span>{t("auth.participation.needHelp.text")} </span>
            <a href={`mailto:${supportEmail}`} className="text-[#169C9D] hover:underline">
              {t("auth.participation.needHelp.link")}
            </a>
        </div>
      </form>
    </div>
  );
};
